/* istanbul ignore file */
/* tslint:disable */
import { array as codecArray } from 'purify-ts/Codec';
import { utils as coreUtils, requestParams } from '@codegrade/api-core';
import { CouponDataParser, CourseCoupon, CourseCouponUsage, PayWithCouponCoursePriceData, StartedTransaction, StartPaymentCoursePriceData, TenantCouponUsage, } from '@codegrade/api-models';
import { parsers } from '@codegrade/utils';
import { makeResponses } from '../response_factory';
const makeResult = coreUtils.makeResult;
const parseJsonResponse = /* #__PURE__ */ parsers.parseJsonResponse;
export function coursePrice(config) {
    const createCouponResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(config, /* #__PURE__ */ parseJsonResponse(CourseCoupon)),
    });
    const getAllCouponsResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(config, 
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(CourseCoupon))),
    });
    const deleteCouponResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(config, () => Promise.resolve(null)),
    });
    const updateCouponResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(config, /* #__PURE__ */ parseJsonResponse(CourseCoupon)),
    });
    const startPaymentResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(config, 
        /* #__PURE__ */ parseJsonResponse(StartedTransaction)),
    });
    const payWithCouponResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(config, 
        /* #__PURE__ */ parseJsonResponse(
        /* #__PURE__ */ parsers.taggedUnion('scope', [
            ['course', CourseCouponUsage],
            ['tenant', TenantCouponUsage],
        ]))),
    });
    return Object.freeze({
        /**
         * Create a new coupon for a course price.
         *
         * @param arg The arguments to the function.
         * @param arg.priceId The price you want to create a coupon for.
         * @param arg.requestBody The body of the request.
         * @returns The coupon created for this course price.
         */
        createCoupon: async ({ priceId, requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/course_prices/${priceId}/coupons/`, createCouponResponse.v, JSON.stringify(CouponDataParser.encode(requestBody)));
            return result;
        },
        /**
         * Get the coupons of a price.
         *
         * @param arg The arguments to the function.
         * @param arg.priceId The price id for which you want to get all coupons.
         * @returns The coupons (that the current user may see) of the price.
         */
        getAllCoupons: async ({ priceId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/course_prices/${priceId}/coupons/`, getAllCouponsResponse.v, undefined);
            return result;
        },
        /**
         * Delete a coupon.
         *
         * @param arg The arguments to the function.
         * @param arg.priceId The id of the price the coupon is connected to.
         * @param arg.couponId The id of the coupon you want to delete.
         * @returns Nothing
         */
        deleteCoupon: async ({ priceId, couponId, }) => {
            const result = await requestParams(config, 'DELETE', `/api/v1/course_prices/${priceId}/coupons/${couponId}`, deleteCouponResponse.v, undefined);
            return result;
        },
        /**
         * Update the given coupon with new values.
         *
         * @param arg The arguments to the function.
         * @param arg.priceId The price to which the coupon is connected.
         * @param arg.couponId The id of the coupon you want to update.
         * @param arg.requestBody The body of the request.
         * @returns The updated coupon
         */
        updateCoupon: async ({ priceId, couponId, requestBody, }) => {
            const result = await requestParams(config, 'PATCH', `/api/v1/course_prices/${priceId}/coupons/${couponId}`, updateCouponResponse.v, JSON.stringify(CouponDataParser.encode(requestBody)));
            return result;
        },
        /**
         * Create a new payment for the current user.
         *
         * @param arg The arguments to the function.
         * @param arg.priceId The price you want to pay for.
         * @param arg.requestBody The body of the request.
         * @returns A transaction for this course price with a `stripe_url` key that can be used to pay. Be careful to check the state of the transaction, as a payment might already be in progress.
         */
        startPayment: async ({ priceId, requestBody, }) => {
            const result = await requestParams(config, 'PUT', `/api/v1/course_prices/${priceId}/pay`, startPaymentResponse.v, JSON.stringify(StartPaymentCoursePriceData.encode(requestBody)));
            return result;
        },
        /**
         * Pay for a course with a coupon.
         *
         * @param arg The arguments to the function.
         * @param arg.priceId The id of the price you want to pay for.
         * @param arg.requestBody The body of the request.
         * @returns Nothing
         */
        payWithCoupon: async ({ priceId, requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/course_prices/${priceId}/pay_with_coupon/`, payWithCouponResponse.v, JSON.stringify(PayWithCouponCoursePriceData.encode(requestBody)));
            return result;
        },
    });
}
